/**
 * <CorrectionMoment> — un temps de l'attente de correction, seul à l'écran (molecule).
 *
 * L'ancien `CorrectionWait` dit une chose à la fois, Sam au-dessus : « Sam corrige ta copie » (il
 * prend la copie), « Ta correction est prête ! » (avant qu'elle ne s'ouvre), ou le mot qui dit qu'elle
 * ne viendra pas. Une seule forme pour les trois : le champion, un titre, une ligne, et les sorties en
 * `children`.
 *
 * `working` : Sam travaille, sans durée promise. Le titre se dit alors en `<WaitingText>`, souligné en
 * boucle, la seule façon d'attendre de l'app (DS, § « Chargements ») ; c'étaient trois points posés nus
 * sous le titre, hors de la bulle qui leur donne leur sens.
 *
 * Sam a un seul visage, sa tête V16 (`<ChampionAvatar>`), le même que dans l'en-tête où il se range
 * ensuite (Wajih, 20/10) : il ne change plus de dessin en route. Une page qui le fait se ranger
 * (`<ChampionStage>`) le dessine elle-même et ne garde ici que les mots (`champion={false}`).
 */
import React from "react"

import { ChampionAvatar } from "../../atoms/Avatar/ChampionAvatar"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { CHAMPION_STAGE_SIZE } from "../ChampionStage"

export type CorrectionMomentTone = "neutral" | "success"

export type CorrectionMomentProps = {
    title: string
    /** Une ligne sous le titre. */
    detail?: string
    /** `success` : le titre à l'encre de progression (« Ta correction est prête ! »). Défaut `neutral`. */
    tone?: CorrectionMomentTone
    /** Sam travaille : le titre se souligne en boucle (`<WaitingText>`). Défaut `false`. */
    working?: boolean
    /** Sam au-dessus des mots. `false` quand la page le dessine elle-même (`<ChampionStage>`). Défaut `true`. */
    champion?: boolean
    /** Les sorties : un bouton, ou rien. */
    children?: React.ReactNode
    testID?: string
}

export function CorrectionMoment(props: CorrectionMomentProps) {
    const { title, detail, tone = "neutral", working = false, champion = true, children, testID } = props
    const color = tone === "success" ? "success-strong" : "text-strong"

    return (
        <Stack gap="4" align="center" testID={testID}>
            {champion && <ChampionAvatar name="sam" size={CHAMPION_STAGE_SIZE} />}
            <Stack gap="2" align="center">
                {working ? (
                    <WaitingText size="text-2xl" weight="bold" color={color} numberOfLines={2}>
                        {title}
                    </WaitingText>
                ) : (
                    <Text size="text-2xl" weight="bold" color={color} center>
                        {title}
                    </Text>
                )}
                {detail != null && detail !== "" && (
                    <Text size="text-md" color="text-muted" center>
                        {detail}
                    </Text>
                )}
            </Stack>
            {children}
        </Stack>
    )
}
